/**
 * Drop-in replacement for react-meta-tags using react-helmet-async.
 * react-meta-tags internally uses ReactDOM.render() which is deprecated in React 18.
 * This wrapper provides the same API using react-helmet-async (React 18 compatible).
 */
import React from 'react'
import { Helmet } from 'react-helmet-async'

// MetaTags: wraps <title> and <meta> elements
// Usage: <MetaTags><title>Page Title</title><meta name="desc" content="..." /></MetaTags>
const MetaTags = ({ children }: { children: React.ReactNode }) => {
  return <Helmet>{children}</Helmet>
}

// ReactTitle: shorthand for setting just the page title
// Usage: <ReactTitle title="Page Title" />
const ReactTitle = ({ title }: { title: string }) => {
  return (
    <Helmet>
      <title>{title}</title>
    </Helmet>
  )
}

// Site-level social preview defaults. The image lives in react-ui/public/.
// Keep these in sync with the baseline tags in public/index.html.
const DEFAULT_SOCIAL_IMAGE = '/ooh-social-image.png'
const DEFAULT_META_TITLE = 'Out of Hand Theater — Setting the Table for Racial Equity'

/**
 * Open Graph requires og:image to be an absolute URL; relative paths are a spec
 * violation and are resolved inconsistently across scrapers.
 *
 * The origin is read from the browser at runtime rather than hardcoded or baked
 * in from a REACT_APP_* var. REACT_APP_* is build-time, so a baked-in host would
 * be wrong on every environment it was not built for -- and hardcoding a host in
 * this repo has broken production before. src/global.js already derives
 * global.domain from window.location.host the same way.
 *
 * Absolute inputs are passed through untouched, so API-supplied image URLs
 * (metaImage.primary.url) are safe to run through this too.
 */
const absoluteUrl = (pathOrUrl?: string | null): string => {
  const value = pathOrUrl || ''
  if (!value) return ''
  if (typeof window === 'undefined' || !window.location) return value
  try {
    return new URL(value, window.location.origin).toString()
  } catch (e) {
    return value
  }
}

/**
 * Canonical address of the current page for og:url -- origin + path only.
 * Query string and hash are dropped: they are tracking/session noise and would
 * split a single shared page into many canonical URLs.
 */
const canonicalUrl = (): string => {
  if (typeof window === 'undefined' || !window.location) return ''
  return `${window.location.origin}${window.location.pathname}`
}

export { MetaTags, ReactTitle, DEFAULT_SOCIAL_IMAGE, DEFAULT_META_TITLE, absoluteUrl, canonicalUrl }
export default MetaTags
